import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Html5QrcodeScanner, Html5QrcodeScannerState } from 'html5-qrcode';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Drawer from '@mui/material/Drawer';
import DialogContent from '@mui/material/DialogContent';
import CloseIcon from '@mui/icons-material/Close';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';

import { ApiParticipant } from '../../contexts/HttpClientContext';
import { ScannerCheckInState } from './EventModalTypes';
import { useTranslation } from 'react-i18next';

import { BarcodeDetector, prepareZXingModule, ZXING_WASM_VERSION } from "barcode-detector/ponyfill";

// Initialize the wasm polyfill using unpkg CDN as recommended in the documentation
prepareZXingModule({
    overrides: {
        locateFile: (path: string, prefix: string) => {
            if (path.endsWith(".wasm")) {
                return `https://unpkg.com/zxing-wasm@${ZXING_WASM_VERSION}/dist/reader/${path}`;
            }
            return prefix + path;
        },
    },
});

// Polyfill the global BarcodeDetector if it doesn't exist
if (!("BarcodeDetector" in window)) {
    // @ts-ignore
    window.BarcodeDetector = BarcodeDetector;
}

const NativeScanner = ({ onScanResult }: { onScanResult: (decodedText: string) => void }) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const requestRef = useRef<number>(0);
    const isScanningActive = useRef(true);

    useEffect(() => {
        let stream: MediaStream | null = null;
        let detector: any = null;

        const initScanner = async () => {
            try {
                // Initialize detector
                // @ts-ignore
                detector = new window.BarcodeDetector({ formats: ['qr_code'] });

                stream = await navigator.mediaDevices.getUserMedia({
                    video: { facingMode: 'environment' }
                });

                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    videoRef.current.play().catch(err => console.error("Video play error:", err));
                }

                const scanFrame = async () => {
                    if (videoRef.current && videoRef.current.readyState === videoRef.current.HAVE_ENOUGH_DATA) {
                        if (isScanningActive.current) {
                            try {
                                // Draw frame to canvas for extraction
                                const canvas = canvasRef.current;
                                const video = videoRef.current;
                                if (canvas && video) {
                                    canvas.width = video.videoWidth;
                                    canvas.height = video.videoHeight;
                                    const ctx = canvas.getContext('2d', { willReadFrequently: true });
                                    if (ctx) {
                                        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                                        const barcodes = await detector.detect(canvas);
                                        if (barcodes && barcodes.length > 0) {
                                            const decodedText = barcodes[0].rawValue;
                                            isScanningActive.current = false;
                                            onScanResult(decodedText);
                                            // Pause scanning for 2 seconds
                                            setTimeout(() => {
                                                isScanningActive.current = true;
                                            }, 2000);
                                        }
                                    }
                                }
                            } catch (e) {
                                console.error("Detection error:", e);
                            }
                        }
                    }
                    requestRef.current = requestAnimationFrame(scanFrame);
                };

                requestRef.current = requestAnimationFrame(scanFrame);

            } catch (err: any) {
                console.error("Camera access denied or error:", err);
                setErrorMsg("Åtkomst till kameran nekades eller ett fel uppstod.");
            }
        };

        initScanner();

        return () => {
            if (requestRef.current) {
                cancelAnimationFrame(requestRef.current);
            }
            if (stream) {
                stream.getTracks().forEach(track => track.stop());
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Box sx={{ width: '100%', backgroundColor: '#000', borderRadius: '8px', position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {errorMsg ? (
                <Alert severity="error">{errorMsg}</Alert>
            ) : (
                <>
                    <video
                        ref={videoRef}
                        playsInline
                        muted
                        style={{ display: 'block', width: '100%', height: 'auto', objectFit: 'cover' }}
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '250px',
                            height: '250px',
                            border: '4px solid rgba(255, 255, 255, 0.7)',
                            borderRadius: '12px',
                            boxShadow: '0 0 0 4000px rgba(0, 0, 0, 0.5)',
                            pointerEvents: 'none'
                        }}
                    />
                    <canvas ref={canvasRef} style={{ display: 'none' }} />
                </>
            )}
        </Box>
    );
};

export const QrCodeScanner = ({
    handleScannerCheckIn,
    handleScannerCheckInPartner,
    scannerCheckInState }
    : {
        handleScannerCheckIn: (scannedText: string, checkOut: boolean) => Promise<void>,
        handleScannerCheckInPartner: (partnerParticipant: ApiParticipant) => Promise<void>,
        scannerCheckInState: ScannerCheckInState
    }) => {
    const useNativeScanner = true;
    const html5QrcodeScannerRef = useRef<Html5QrcodeScanner>(null);
    const scannerIsOpenRef = useRef(true);
    const [scannedText, setScannedText] = useState("");
    const { t } = useTranslation();

    const handleScanResult = (decodedText: string) => {

        try {

            var context = new AudioContext();
            var oscillator = context.createOscillator();
            oscillator.type = "sine";
            oscillator.frequency.value = 800;
            oscillator.connect(context.destination);
            oscillator.start();
            // Beep for 100 milliseconds
            setTimeout(function () {
                oscillator.stop();
            }, 100);
        } catch (error) {
            console.error("Could not play sound", error);
        }

        console.log("scanned text", decodedText);
        setScannedText(decodedText);

        if (html5QrcodeScannerRef.current) {
            const scannerState = html5QrcodeScannerRef.current.getState();

            if (scannerState === Html5QrcodeScannerState.SCANNING) {
                html5QrcodeScannerRef.current.pause();
            }
        }
        handleScannerCheckIn(decodedText, false).then(() => {
            setTimeout(() => {
                if (html5QrcodeScannerRef.current) {
                    if (html5QrcodeScannerRef.current.getState() === Html5QrcodeScannerState.PAUSED) {
                        html5QrcodeScannerRef.current.resume();
                    }
                }
            }, 2000);
        });


    }


    useEffect(() => {
        if (useNativeScanner) return;
        scannerIsOpenRef.current = true;

        if (!html5QrcodeScannerRef.current) {
            html5QrcodeScannerRef.current = new Html5QrcodeScanner(
                "scanner",
                //{ fps: 1, qrbox: qrboxFunction, aspectRatio: 1 },
                { fps: 10, qrbox: 220 },
                false
            );
            html5QrcodeScannerRef.current.render(
                (decodedText, _) => handleScanResult(decodedText),
                (errorMessage) => {
                    console.debug(`Error: ${errorMessage}`);
                }
            );
        }

        return () => {
            console.log("closing scanner component");
            scannerIsOpenRef.current = false;
            setScannedText("");

            if (html5QrcodeScannerRef.current) {
                const scannerState = html5QrcodeScannerRef.current.getState();
                console.log(scannerState);
                html5QrcodeScannerRef.current.clear().catch(async (error) => {
                    console.error(`Failed to clear scanner: ${error}`);
                });
                html5QrcodeScannerRef.current = null;
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [useNativeScanner]);

    return (
        <>
            {useNativeScanner ? (
                <NativeScanner onScanResult={handleScanResult} />
            ) : (
                <div id="scanner"></div>
            )}
            {scannedText.length > 0 && scannerCheckInState.status !== "idle" &&
                <Drawer
                    anchor='bottom'
                    open={scannedText.length > 0}
                    onClose={() => setScannedText("")}
                    sx={{ zIndex: 2000 }}
                >
                    <IconButton
                        aria-label="close"
                        onClick={() => setScannedText("")}
                        sx={(theme) => ({
                            position: 'absolute',
                            right: 18,
                            top: 14,
                            color: theme.palette.grey[500],
                        })}
                    >
                        <CloseIcon />
                    </IconButton>
                    <DialogContent>

                        <Box>
                            {scannerCheckInState.status === "success" &&
                                <Alert severity="success" variant='outlined'>
                                    <AlertTitle>
                                        {t("scanner.status_checked_in")}
                                    </AlertTitle>
                                    <Typography component="p"><b>{scannerCheckInState?.booking?.name}</b> är nu incheckad.</Typography>
                                    <Typography component="p">Medlemsnummer: <b>{scannerCheckInState?.booking?.membershipNumber}</b></Typography>
                                    <Typography component="p">Bokningsinformation: {scannerCheckInState?.booking?.bookingInfo}</Typography>
                                    <Button onClick={() => handleScannerCheckIn(scannerCheckInState.booking!.membershipNumber, true)} variant='outlined' sx={{ marginTop: "10px" }}>{t("participants.check_out")}</Button>
                                </Alert>
                            }
                            {scannerCheckInState.status === "alreadyCheckedIn" &&
                                <Alert severity="info" variant='outlined'>
                                    <AlertTitle>
                                        {t("scanner.status_already_checked_in")}
                                    </AlertTitle>
                                    <Typography component="p"><b>{scannerCheckInState.booking!.name}</b> är redan incheckad.</Typography>
                                    <Typography component="p">Medlemsnummer: <b>{scannerCheckInState?.booking?.membershipNumber}</b></Typography>
                                    <Typography component="p">Bokningsinformation: {scannerCheckInState?.booking?.bookingInfo}</Typography>
                                    <Button onClick={() => handleScannerCheckIn(scannerCheckInState.booking!.membershipNumber, true)} variant='outlined' sx={{ marginTop: "10px" }}>{t("participants.check_out")}</Button>
                                </Alert>
                            }
                            {scannerCheckInState.status === "notBooked" &&
                                <Alert severity="warning" variant='outlined'>
                                    <AlertTitle>
                                        Bokning saknas!
                                    </AlertTitle>
                                    <Typography component="p">Ingen bokning hittades för {scannedText}</Typography>
                                </Alert>
                            }
                            {scannerCheckInState.status === "invalidFormat" &&
                                <Alert severity="warning" variant='outlined'>
                                    <AlertTitle>
                                        Felaktigt format!
                                    </AlertTitle>
                                    <Typography component="p">Det avlästa värdet "{scannedText}" har ett felaktigt eller okänt format.</Typography>
                                </Alert>
                            }
                            {scannerCheckInState.status === "error" &&
                                <Alert severity="error" variant='outlined'>
                                    <AlertTitle>
                                        {t("scanner.status_error")}
                                    </AlertTitle>
                                    <Typography component="p">Ett oväntat fel har inträffat.</Typography>
                                </Alert>
                            }
                            {scannerCheckInState.status === "removed" &&
                                <Alert severity="info" variant='outlined'>
                                    <AlertTitle>
                                        {t("participants.status_checked_out")}
                                    </AlertTitle>
                                    <Typography component="p"><b>{scannerCheckInState.booking!.name}</b> är nu utcheckad.</Typography>
                                    <Button onClick={() => handleScannerCheckIn(scannerCheckInState.booking!.membershipNumber, false)} variant='outlined' sx={{ marginTop: "10px" }}>{t("participants.check_in")}</Button>
                                </Alert>
                            }

                            {(scannerCheckInState.status === "success-ask-partner"
                                || scannerCheckInState.status === "partner-checkIn-loading")
                                && scannerCheckInState.partnerParticipant &&
                                <Alert severity="success" variant='outlined'>
                                    <AlertTitle>
                                        {t("scanner.status_checked_in")}
                                    </AlertTitle>
                                    <Typography component="p"><b>{scannerCheckInState?.booking?.name}</b> är nu incheckad.</Typography>
                                    <Typography component="p">Medlemsnummer: <b>{scannerCheckInState?.booking?.membershipNumber}</b></Typography>
                                    <Typography component="p">Bokningsinformation: {scannerCheckInState?.booking?.bookingInfo}</Typography>
                                    <Button onClick={() => handleScannerCheckIn(scannerCheckInState.booking!.membershipNumber, true)} variant='outlined' sx={{ marginTop: "10px", marginBottom: "10px" }}>{t("participants.check_out")}</Button>
                                    <Divider sx={{ marginBottom: "10px" }} />
                                    <Typography component="p">{t("scanner.status_ask_partner_desc", { name: scannerCheckInState.partnerParticipant?.name })}</Typography>
                                    <Typography component="p">Bokningsinformation: {scannerCheckInState.partnerParticipant?.bookingInfo}</Typography>
                                    {scannerCheckInState.status === "partner-checkIn-loading" &&
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <CircularProgress size={'30px'} />
                                        </Box>
                                    }
                                    {scannerCheckInState.status === "success-ask-partner" &&
                                        <Button onClick={() => handleScannerCheckInPartner(scannerCheckInState.partnerParticipant!)} variant='outlined' sx={{ marginTop: "10px" }}>{t("scanner.yes_check_in_partner")}</Button>
                                    }
                                </Alert>
                            }

                            {scannerCheckInState.status === "partner-success" && scannerCheckInState.partnerParticipant &&
                                <Alert variant='outlined' severity='success'>
                                    <AlertTitle>{t("scanner.partner_checked_in")}</AlertTitle>
                                    <Typography component="p">{scannerCheckInState.partnerParticipant.name} är nu incheckad.</Typography>
                                    <Typography component="p">Bokningsinformation: {scannerCheckInState.partnerParticipant.bookingInfo} </Typography>
                                </Alert>
                            }

                            {scannerCheckInState.status === "partner-alreadyCheckedIn" &&
                                <Alert variant='outlined' severity='info'>{t("participants.status_partner_already_checked_in")}</Alert>
                            }
                            {scannerCheckInState.status === "partner-error" &&
                                <Alert variant='outlined' severity='error'>
                                    <AlertTitle>{t("scanner.partner_error")}</AlertTitle>
                                    <Typography component="p">{t("scanner.partner_error_desc")}</Typography>
                                    {scannerCheckInState.partnerParticipant &&
                                        <Button onClick={() => handleScannerCheckInPartner(scannerCheckInState.partnerParticipant!)} variant='outlined'>{t("scanner.try_again")}</Button>
                                    }
                                </Alert>
                            }
                        </Box>
                    </DialogContent>
                </Drawer>
            }
        </>
    );
};
